import { fetchDatasetStream } from "@/api/dataset/fetchDatasetStream";
import { useMutationDatasetMetadata } from "@/api/dataset/useDatasetAPI";
import { useGetUserProfileAPI } from "@/api/userProfile/useUserProfileAPI";
import { EspatialAggregationType } from "@/types/common";
import {
  DatasetAttributeSchema,
  DatasetData,
  DatasetMetadata
} from "@/types/dataset";
import { DatasetConfigResponse } from "@/types/project";
import {
  embedLegendConfigImages,
  mapLegendToStyleConfig,
  resolveMarkerAssetUrl
} from "@/utils/configurationMapper";
import { generateDatasetData } from "@/utils/generateDatasetData";

import { mapDatasetWeights } from "../useAnalysisConfiguration";
import { useAppFeatures } from "../useAppFeatures";
import { buildDatasetFilter, getAggregationConfig } from "./utils";

/** Loads a saved dataset config onto the latest dataset metadata and data */
export const useRestoreDataset = () => {
  const {
    isCustomMapVisualizationLegendEnabled,
    isPoiNumericAttributesEnabled
  } = useAppFeatures();
  const { data: userProfileData } = useGetUserProfileAPI();
  const { mutateAsync: getDatasetMetadata } = useMutationDatasetMetadata();

  /**
   * Streams the data of a saved dataset with its saved filters, grid config and
   * group_by, plus the schema its categories are built from.
   */
  const fetchDatasetData = async (
    dataset: DatasetConfigResponse,
    metadata: DatasetMetadata
  ) => {
    const filters = buildDatasetFilter(dataset.attributes, metadata?.schema);

    // H3 datasets (user data included) and timeseries datasets with a group_by
    // are streamed aggregated, so their schema needs the same grid config and
    // group_by or the restored categories describe the raw columns instead.
    const aggregatedMetadata =
      metadata?.spatial_aggregation_type === EspatialAggregationType.H3 ||
      (dataset.group_by && dataset.group_by !== "none" && metadata?.timeseries)
        ? await getDatasetMetadata({
            id: metadata?.id,
            h3Configuration: getAggregationConfig(dataset?.grid_config),
            show_id: false,
            group_by: dataset.group_by
          })
        : metadata;

    const res = await fetchDatasetStream({
      datasetId: dataset.id,
      filters,
      timezone: userProfileData?.timezone,
      h3Configuration: getAggregationConfig(dataset?.grid_config),
      group_by: dataset?.group_by,
      generateDatasetData
    });

    return { aggregatedMetadata, data: res?.rawDataset };
  };

  /** Fetches the latest metadata of a saved dataset, then its data */
  const fetchDatasetForRestore = async (
    dataset: DatasetConfigResponse,
    shouldSkipData?: (
      dataset: DatasetConfigResponse,
      metadata: DatasetMetadata
    ) => boolean
  ) => {
    const metadata = await getDatasetMetadata({ id: dataset.id });

    if (shouldSkipData?.(dataset, metadata)) {
      return { metadata, aggregatedMetadata: metadata, data: undefined };
    }

    return { metadata, ...(await fetchDatasetData(dataset, metadata)) };
  };

  const getDatasetDataAndMetadata = async (
    reversedDatasets: DatasetConfigResponse[],
    shouldSkipData?: (
      dataset: DatasetConfigResponse,
      metadata: DatasetMetadata
    ) => boolean
  ) => {
    const datasetsData: (DatasetData | undefined)[] = [];
    const datasetsDetail: DatasetMetadata[] = [];
    const datasetsDetailAggregated: DatasetMetadata[] = [];

    // Fetched one by one. A skipped dataset gets an empty data slot, so every
    // array stays aligned with reversedDatasets.
    for (const dataset of reversedDatasets) {
      const { metadata, aggregatedMetadata, data } =
        await fetchDatasetForRestore(dataset, shouldSkipData);
      datasetsDetail.push(metadata);
      datasetsDetailAggregated.push(aggregatedMetadata);
      datasetsData.push(data);
    }

    return { datasetsData, datasetsDetail, datasetsDetailAggregated };
  };

  /**
   * Arguments shared by both restore paths (open project, open analysis config)
   * when a saved dataset is re-added.
   */
  const buildRestoredDatasetArgs = async (
    dataset: DatasetConfigResponse,
    datasetMetadata: DatasetMetadata,
    datasetData: DatasetData,
    attributesSchema: DatasetAttributeSchema[]
  ) => {
    const datasetWeights = mapDatasetWeights(
      dataset,
      datasetMetadata?.schema,
      isPoiNumericAttributesEnabled
    );

    const legendStyleConfig = isCustomMapVisualizationLegendEnabled
      ? await embedLegendConfigImages(
          mapLegendToStyleConfig(dataset),
          resolveMarkerAssetUrl
        )
      : undefined;

    return {
      datasetMetadata: {
        ...datasetMetadata,
        display_name: dataset.display_name ?? dataset.name
      },
      datasetData,
      savedFilters: buildDatasetFilter(
        dataset.attributes,
        datasetMetadata?.schema
        // Filters are stored top-to-bottom in project config, but dataset
        // history grows bottom-to-top (each entry prepended). Reversing
        // here aligns the filter order with the history display order.
      ).reverse(),
      attributesSchema, // filtered metadata schema for h3 dataset
      h3Configuration: getAggregationConfig(dataset?.grid_config),
      group_by: dataset.group_by,
      weightValues: {
        // `dataset.weight` is 0 for projects saved before this field was
        // introduced. Fall back to the number of weighted attributes so
        // the weight panel initialises to a sensible non-zero value.
        weight: (
          dataset.weight ||
          Object.keys(datasetWeights).length ||
          1
        ).toString(),
        attributes: datasetWeights,
        include_scoring: dataset?.include_scoring
      },
      styleConfig: {
        colorFamily: dataset?.color_scale,
        opacity: dataset?.opacity,
        selectedCategory: dataset?.color_based,
        labelConfig: dataset?.label_config,
        colorSteps: dataset?.color_steps,
        rankConfig: {
          isEnabled: dataset.label_config?.enable_ranking ?? false,
          maxRanking: dataset.label_config?.max_ranking ?? 10
        },
        strokeConfig:
          dataset.spatial_aggregation_type === "line"
            ? {
                width: dataset?.stroke_width ?? 1,
                opacity: dataset?.opacity ?? 0.7
              }
            : undefined,
        // Spreading `undefined` is a no-op, so no `?? {}` fallback needed.
        ...legendStyleConfig
      },
      is_global_admin_area: dataset?.is_global_admin_area ?? false
    };
  };

  return {
    fetchDatasetData,
    getDatasetDataAndMetadata,
    buildRestoredDatasetArgs
  };
};
